(() => {
  if (window.TDMVariantMediaFilter) {
    if (typeof window.TDMVariantMediaFilter.refresh === 'function') {
      window.TDMVariantMediaFilter.refresh();
    }
    return;
  }

  const REF_BACKUP_ATTR = 'data-variant-media-ref';
  const READY_ATTR = 'data-variant-images-initialized';
  const HIDDEN_CLASS = 'variant-media-hidden';

  const normalize = (value) =>
    String(value || '')
      .toLowerCase()
      .replace(/[#\s_-]/g, '');

  const unique = (values) => Array.from(new Set(values.filter(Boolean)));

  const getSelectedValues = (scope) => {
    if (!scope) return [];
    const values = [];
    const radios = Array.from(scope.querySelectorAll('input[type="radio"]:checked'));
    const selects = Array.from(scope.querySelectorAll('select'));

    radios.forEach((radio) => values.push(radio.value));
    selects.forEach((select) => {
      const option = select.options[select.selectedIndex];
      if (option) values.push(option.value);
    });

    return unique(values);
  };

  const matchAlt = (alt, keys) => {
    if (!alt || !keys.length) return false;
    const altCompact = normalize(alt);
    const altLower = String(alt).toLowerCase();
    const hasColorToken = altLower.includes('color');

    return keys.some((key) => {
      const keyCompact = normalize(key);
      if (!keyCompact) return false;
      if (hasColorToken && altCompact.includes(`color${keyCompact}`)) return true;
      return altCompact.includes(keyCompact);
    });
  };

  // product-media keeps the colour token (e.g. "#color_odeon-NATUREL") beside a
  // readable alt; older markup only has the alt. Shopify's image_tag writes the
  // `data_variant_alt:` argument with underscores, so read both spellings.
  const readAlt = (img) =>
    img?.getAttribute('data-variant-alt') ||
    img?.getAttribute('data_variant_alt') ||
    img?.getAttribute('alt') ||
    '';

  const getAltFromElement = (element) => {
    if (!element) return '';
    if (element instanceof HTMLImageElement) return readAlt(element);
    return readAlt(element.querySelector('img'));
  };

  const toggleHidden = (element, shouldHide) => {
    if (!element) return;
    const ref = element.getAttribute('ref');

    if (shouldHide) {
      if (ref) {
        element.setAttribute(REF_BACKUP_ATTR, ref);
        element.removeAttribute('ref');
      }
      element.classList.add(HIDDEN_CLASS);
      element.setAttribute('hidden', '');
      element.setAttribute('aria-hidden', 'true');
      element.removeAttribute('reveal');
    } else {
      const storedRef = element.getAttribute(REF_BACKUP_ATTR);
      if (storedRef && !ref) {
        element.setAttribute('ref', storedRef);
      }
      element.classList.remove(HIDDEN_CLASS);
      element.removeAttribute('hidden');
      element.setAttribute('aria-hidden', 'false');
    }
  };

  const filterGallery = (gallery) => {
    if (!gallery) return;
    gallery.setAttribute(READY_ATTR, 'false');

    const section = gallery.closest('.shopify-section') || document;
    const variantPicker =
      section.querySelector('variant-picker') ||
      document.querySelector('variant-picker[data-template-product-match="true"]') ||
      document.querySelector('variant-picker');

    const selectedValues = getSelectedValues(variantPicker);
    const slides = Array.from(gallery.querySelectorAll('slideshow-slide'));
    const dotGroups = Array.from(gallery.querySelectorAll('.slideshow-controls__dots'));
    const thumbGroups = Array.from(gallery.querySelectorAll('.slideshow-controls__thumbnails'));
    const dialogThumbs = Array.from(gallery.querySelectorAll('.dialog-thumbnails-list__thumbnail'));
    const gridItems = Array.from(gallery.querySelectorAll('.media-gallery__grid li'));
    const zoomItems = Array.from(gallery.querySelectorAll('.dialog-zoomed-gallery li'));

    if (!slides.length || !selectedValues.length) {
      slides.forEach((slide) => toggleHidden(slide, false));
      dotGroups.forEach((group) => {
        group.querySelectorAll('button').forEach((dot) => toggleHidden(dot, false));
        group.querySelectorAll('li').forEach((item) => toggleHidden(item, false));
      });
      thumbGroups.forEach((group) => {
        group.querySelectorAll('.slideshow-controls__thumbnail').forEach((thumb) => toggleHidden(thumb, false));
      });
      dialogThumbs.forEach((thumb) => toggleHidden(thumb, false));
      gridItems.forEach((item) => toggleHidden(item, false));
      zoomItems.forEach((item) => toggleHidden(item, false));
      gallery.setAttribute(READY_ATTR, 'true');
      return;
    }

    const slideMatches = slides.map((slide) => matchAlt(getAltFromElement(slide), selectedValues));
    const hasMatches = slideMatches.some(Boolean);

    if (!hasMatches) {
      slides.forEach((slide) => toggleHidden(slide, false));
      dotGroups.forEach((group) => {
        group.querySelectorAll('button').forEach((dot) => toggleHidden(dot, false));
        group.querySelectorAll('li').forEach((item) => toggleHidden(item, false));
      });
      thumbGroups.forEach((group) => {
        group.querySelectorAll('.slideshow-controls__thumbnail').forEach((thumb) => toggleHidden(thumb, false));
      });
      dialogThumbs.forEach((thumb) => toggleHidden(thumb, false));
      gridItems.forEach((item) => toggleHidden(item, false));
      zoomItems.forEach((item) => toggleHidden(item, false));
      gallery.setAttribute(READY_ATTR, 'true');
      return;
    }

    let visibleIndex = 0;
    const visibleIndexBySlide = slides.map((slide, index) => {
      const isVisible = slideMatches[index];
      if (!isVisible) return -1;
      const currentIndex = visibleIndex;
      visibleIndex += 1;
      return currentIndex;
    });

    slides.forEach((slide, index) => toggleHidden(slide, !slideMatches[index]));
    dotGroups.forEach((group) => {
      const dots = Array.from(group.querySelectorAll('button'));
      dots.forEach((dot, index) => {
        const isVisible = slideMatches[index];
        toggleHidden(dot, !isVisible);
        const dotItem = dot.closest('li');
        if (dotItem) toggleHidden(dotItem, !isVisible);
        if (isVisible) {
          dot.setAttribute('on:click', `/select/${visibleIndexBySlide[index]}`);
        }
      });
    });

    thumbGroups.forEach((group) => {
      const thumbs = Array.from(group.querySelectorAll('.slideshow-controls__thumbnail'));
      thumbs.forEach((thumb, index) => {
        const isVisible = slideMatches[index];
        toggleHidden(thumb, !isVisible);
        if (isVisible) {
          thumb.setAttribute('on:click', `/select/${visibleIndexBySlide[index]}`);
        }
      });
    });

    dialogThumbs.forEach((thumb, index) => {
      const isVisible = slideMatches[index];
      toggleHidden(thumb, !isVisible);
      if (isVisible) {
        thumb.setAttribute('on:click', `/handleThumbnailClick/${visibleIndexBySlide[index]}`);
        thumb.setAttribute('on:pointerenter', `/handleThumbnailPointerEnter/${visibleIndexBySlide[index]}`);
      }
    });

    gridItems.forEach((item, index) => {
      const isVisible = slideMatches[index];
      toggleHidden(item, !isVisible);
      if (isVisible) {
        const zoomButton = item.querySelector('.product-media-container__zoom-button');
        if (zoomButton) {
          zoomButton.setAttribute('on:click', `/zoom/${visibleIndexBySlide[index]}`);
        }
      }
    });

    zoomItems.forEach((item, index) => {
      const isVisible = slideMatches[index];
      toggleHidden(item, !isVisible);
    });

    const firstVisibleIndex = visibleIndexBySlide.find((index) => index >= 0);
    const slideshow = gallery.querySelector('slideshow-component');

    if (slideshow && firstVisibleIndex !== undefined && firstVisibleIndex >= 0) {
      queueMicrotask(() => {
        if (typeof slideshow.select === 'function') {
          slideshow.select(firstVisibleIndex, undefined, { animate: false });
        } else {
          slideshow.setAttribute('initial-slide', String(firstVisibleIndex));
        }
      });
    }

    gallery.setAttribute(READY_ATTR, 'true');
  };

  const refresh = () => {
    document
      .querySelectorAll('media-gallery[data-variant-media-filter="true"]')
      .forEach((gallery) => filterGallery(gallery));
  };

  let rafId = null;
  const scheduleRefresh = () => {
    if (rafId) return;
    rafId = requestAnimationFrame(() => {
      rafId = null;
      refresh();
    });
  };

  window.TDMVariantMediaFilter = { refresh: scheduleRefresh };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleRefresh);
  } else {
    scheduleRefresh();
  }

  document.addEventListener('change', (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('variant-picker')) {
      scheduleRefresh();
    }
  });

  document.addEventListener('variant:update', scheduleRefresh);
})();
